import { ViolinVoice } from './voices/ViolinVoice.js';
import { ShellVoice } from './voices/ShellVoice.js';
import { BellVoice } from './voices/BellVoice.js';
import { PadVoice } from './voices/PadVoice.js';

/**
 * Everything Isidora is heard through. No files: the whole city is
 * synthesised, so it weighs nothing and never loads.
 *
 * Signal path
 *   voices ─┬─> dry ──────────────┐
 *           └─> send ─> reverb ───┤
 *                                 ├─> colour (lowpass) ─> master ─> limiter ─> out
 *   pad / drone ───────────────────┘
 *
 * The context cannot open before a gesture, so `unlock()` is wired to the
 * first wheel, pointer or key the traveller gives us.
 */
export class AudioEngine {
  constructor(ctx) {
    this.app = ctx;
    this.cfg = ctx.settings.audio;
    this.bus = ctx.bus;
    this.ready = false;
    this.muted = false;
    this.ac = null;
    this.violins = new Map();
    this._noise = null;
    this._unlockBound = null;
  }

  /** Wire the first gesture on `el` to open the context. Idempotent. */
  arm(el) {
    if (this._unlockBound) return;
    const go = () => {
      this.unlock();
    };
    this._unlockBound = go;
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
      el.addEventListener(type, go, { once: true, passive: true });
      globalThis.addEventListener(type, go, { once: true, passive: true });
    }
  }

  async unlock() {
    if (this.ready) return;
    const Ctx = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    if (!Ctx) return;
    this.ac = new Ctx({ latencyHint: 'interactive' });
    if (this.ac.state === 'suspended') await this.ac.resume().catch(() => {});

    const ac = this.ac;
    const now = ac.currentTime;

    this.limiter = ac.createDynamicsCompressor();
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 14;
    this.limiter.ratio.value = 7;
    this.limiter.attack.value = 0.004;
    this.limiter.release.value = 0.28;
    this.limiter.connect(ac.destination);

    this.master = ac.createGain();
    this.master.gain.setValueAtTime(0.0001, now);
    this.master.gain.exponentialRampToValueAtTime(this.cfg.master, now + 3.2);
    this.master.connect(this.limiter);

    // One filter over everything: the air of the stairwell, which thickens
    // as the traveller descends and closes right down inside a telescope.
    this.colour = ac.createBiquadFilter();
    this.colour.type = 'lowpass';
    this.colour.frequency.value = 9000;
    this.colour.Q.value = 0.4;
    this.colour.connect(this.master);

    this.dry = ac.createGain();
    this.dry.gain.value = 0.82;
    this.dry.connect(this.colour);

    this.reverb = ac.createConvolver();
    this.reverb.buffer = this._impulse(this.cfg.reverbSeconds, this.cfg.reverbDecay);
    this.reverbGain = ac.createGain();
    this.reverbGain.gain.value = 0.9;
    this.reverb.connect(this.reverbGain);
    this.reverbGain.connect(this.colour);

    this.send = ac.createGain();
    this.send.gain.value = 0.55;
    this.send.connect(this.reverb);

    this._noise = this._noiseBuffer(3);

    this.pad = new PadVoice(this);
    this.pad.start();

    this.ready = true;
    this.bus.emit('audio:ready', {});
  }

  get destinationPair() {
    return { dry: this.dry, send: this.send };
  }

  get noiseBuffer() {
    return this._noise;
  }

  /** A hall that is mostly stone and a little water. */
  _impulse(seconds, decay) {
    const ac = this.ac;
    const rate = ac.sampleRate;
    const length = Math.floor(rate * seconds);
    const buf = ac.createBuffer(2, length, rate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      // A short pre-delay keeps the early reflections off the attack.
      const pre = Math.floor(rate * 0.022 * (ch ? 1.3 : 1));
      for (let i = 0; i < length; i++) {
        if (i < pre) {
          data[i] = 0;
          continue;
        }
        const x = (i - pre) / (length - pre);
        const env = Math.pow(1 - x, decay);
        // A few discrete slaps early on: the turns of the stairwell.
        const slap =
          x < 0.08 ? 1 + 0.9 * Math.sin(x * 240 + ch) * Math.exp(-x * 40) : 1;
        data[i] = (Math.random() * 2 - 1) * env * slap;
      }
    }
    return buf;
  }

  _noiseBuffer(seconds) {
    const rate = this.ac.sampleRate;
    const buf = this.ac.createBuffer(1, Math.floor(rate * seconds), rate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      // Mildly pink: bow noise is not white.
      last = 0.96 * last + 0.04 * w;
      d[i] = (w * 0.4 + last * 2.6) * 0.5;
    }
    return buf;
  }

  // ── Voices ────────────────────────────────────────────────────────────────

  /** Create or fetch the standing voice of one violin on the stair. */
  violin(id, hz) {
    if (!this.ready) return null;
    let v = this.violins.get(id);
    if (!v) {
      v = new ViolinVoice(this, hz);
      v.start();
      this.violins.set(id, v);
    }
    return v;
  }

  shell({ pan = 0, bright = 0.5, gain = 0.6 } = {}) {
    if (!this.ready || this.muted) return;
    new ShellVoice(this).play({ pan, bright, gain });
  }

  bell({ hz = 880, gain = 0.5, pan = 0, ratio = 2.41 } = {}) {
    if (!this.ready || this.muted) return;
    new BellVoice(this).play({ hz, gain, pan, ratio });
  }

  // ── Global shaping ────────────────────────────────────────────────────────

  /** Called every frame by the App. */
  update(dt, state) {
    if (!this.ready) return;
    const now = this.ac.currentTime;
    const t = state.t;

    // The air closes as the evening does; a telescope closes it further.
    const open = 9000 - t * 4200;
    const target = state.iris > 0.01 ? open * (1 - state.iris * 0.72) + 900 : open;
    this.colour.frequency.setTargetAtTime(target, now, 0.25);

    // Reverb grows: the stairwell is taller behind you than in front.
    this.send.gain.setTargetAtTime(0.42 + t * 0.4, now, 0.6);

    this.pad?.update(dt, state);
  }

  setMuted(muted) {
    this.muted = muted;
    if (!this.ready) return;
    const now = this.ac.currentTime;
    this.master.gain.setTargetAtTime(muted ? 0.0001 : this.cfg.master, now, 0.25);
  }

  dispose() {
    for (const v of this.violins.values()) v.stop();
    this.violins.clear();
    this.pad?.stop();
    this.ac?.close().catch(() => {});
    this.ready = false;
  }
}
